import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { Segmented, message, Space, Spin } from 'antd';
import { useState } from 'react';

import { Button } from '../../../../components/index';
import { useChangePagination } from '../../../../hooks';
import { useGetBerthsQuery } from '../../../../services/dockingPlacesApi';
import { useLazyExportRegistersQuery } from '../../../../services/exportApi';
import { DockingPlace } from '../../../../types';
import { PanelHeader } from '../panelHeader';
import { RegistersDrawer } from '../registersDrawer';
import { RegistersTable } from '../registersTable';
import { anchoringColumns } from './anchoringColumns';
import { BerthForm } from './berthForm';
import { columns } from './columns';

type PlaceType = 'BERTH' | 'ANCHORING';

export function Berth() {
  const [selectedBerth, setSelectedBerth] = useState({} as DockingPlace);
  const [searchQueryTerm, setSearchQueryTerm] = useState('');
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [placeType, setPlaceType] = useState<PlaceType>('BERTH');

  const { onChangePagination, queryPage, setQueryPage } = useChangePagination();
  const { data: berthData, isFetching: isFetchingBerthData } =
    useGetBerthsQuery({
      placeType,
      berthName: searchQueryTerm,
      // O cadastro exibe também os inativos, para consulta e reativação
      active: 'ALL',
      page_size: 10,
      page: onChangePagination(),
    });

  const [exportRegisters, { isLoading: isExporting }] =
    useLazyExportRegistersQuery();

  async function handleExport() {
    try {
      await exportRegisters({
        exportType: 'berths',
        params: { search: searchQueryTerm },
      }).unwrap();
      message.success(
        'Exportação em processamento. Você a receberá em seu e-mail em breve.',
        5
      );
    } catch (error) {
      message.error('Erro ao solicitar a exportação. Tente novamente.');
    }
  }

  function actionButtons() {
    return (
      <Space size={18}>
        <Button
          icon={<DownloadOutlined />}
          size="large"
          onClick={handleExport}
          loading={isExporting}
        >
          Exportar XLS
        </Button>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          size="large"
          onClick={() => setIsFormVisible(true)}
        >
          {placeType === 'BERTH' ? 'Novo berço' : 'Novo fundeio'}
        </Button>
      </Space>
    );
  }

  function onChangePlaceType(value: string | number) {
    setPlaceType(value as PlaceType);
    setQueryPage(1);
  }

  function onSelectBerth(berth: DockingPlace) {
    setSelectedBerth({ ...berth });
    setIsEdit(true);
    setIsFormVisible(true);
  }

  function onCloseForm() {
    setIsFormVisible(false);
    setSelectedBerth({} as DockingPlace);
    setIsEdit(false);
  }

  function searchData(value: any) {
    setQueryPage(1);
    setSearchQueryTerm(value);
  }

  return (
    <>
      <RegistersDrawer visible={isFormVisible} width={840}>
        <BerthForm
          onClose={onCloseForm}
          isEdit={isEdit}
          selectedBerth={selectedBerth}
          placeType={placeType}
        />
      </RegistersDrawer>
      <PanelHeader
        panelName={placeType === 'BERTH' ? 'berços' : 'fundeios'}
        onSearch={searchData}
        actionButtons={actionButtons()}
      />
      <div style={{ marginBottom: '16px' }}>
        <Segmented
          size="large"
          value={placeType}
          onChange={onChangePlaceType}
          options={[
            { label: 'Berços', value: 'BERTH' },
            { label: 'Fundeios', value: 'ANCHORING' },
          ]}
        />
      </div>
      <div
        style={{
          opacity: isFetchingBerthData ? 0.5 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isFetchingBerthData ? 'none' : 'auto',
        }}
      >
        <RegistersTable
          rowKey="id"
          columns={placeType === 'BERTH' ? columns : anchoringColumns}
          onSelectItem={onSelectBerth}
          dataSource={berthData?.results || []}
          onChangePagination={onChangePagination}
          total={berthData?.count}
          isLoading={isFetchingBerthData}
          queryPage={queryPage}
          showSizeChanger={false}
        />
      </div>
    </>
  );
}
